import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ClockIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { DriveEntry, FileVersionRow } from '@/lib/drive/driveClient';
import { driveClient } from '@/lib/drive/driveClient';
import { formatBytes, formatTimestamp } from '@/lib/drive/format';

interface DriveVersionsDialogProps {
  entry: DriveEntry | null;
  onClose: () => void;
  onRestored: () => void;
}

/**
 * Version history: every previous content of the file, with restore, download,
 * delete — and the door for uploading new content (which snapshots the current
 * bytes as the next history row).
 */
export function DriveVersionsDialog({ entry, onClose, onRestored }: DriveVersionsDialogProps) {
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = useState<FileVersionRow[]>([]);
  const [current, setCurrent] = useState<FileVersionRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!entry) return;
    setLoading(true);
    setError(null);
    try {
      const data = await driveClient.listVersions(entry.path);
      setVersions(data.versions ?? []);
      setCurrent(data.current ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [entry]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!entry) return null;

  const act = async (action: () => Promise<void>) => {
    try {
      await action();
      await load();
      onRestored();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Transition show as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-40 overflow-hidden" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="absolute inset-0 bg-overlay" />
        </Transition.Child>
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-xl border border-border-color bg-card shadow-xl">
              <div className="flex flex-nowrap items-center justify-between gap-3 border-b border-border-color px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <ClockIcon className="h-4 w-4 flex-shrink-0 text-accent" />
                  <Dialog.Title className="truncate whitespace-nowrap text-sm font-medium text-foreground">
                    {t('drive.versionsTitle', { name: entry.name })}
                  </Dialog.Title>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center p-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-border-color border-t-accent" />
                  </div>
                ) : (
                  <ul className="divide-y divide-border-color">
                    {current && (
                      <VersionRow
                        key="current"
                        row={current}
                        label={t('drive.versionsCurrent')}
                        locale={i18n.resolvedLanguage}
                      />
                    )}
                    {versions.length === 0 && !current && (
                      <li className="p-6 text-center text-sm text-muted-foreground">{t('drive.versionsEmpty')}</li>
                    )}
                    {versions.map((row) => (
                      <VersionRow
                        key={row.id}
                        row={row}
                        label={t('drive.versionsRow', { version: row.version })}
                        locale={i18n.resolvedLanguage}
                        actions={
                          <div className="flex flex-shrink-0 items-center gap-0.5">
                            <button
                              type="button"
                              title={t('drive.versionsRestore')}
                              onClick={() => void act(() => driveClient.restoreVersion(entry.path, row.id))}
                              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title={t('drive.download')}
                              onClick={() =>
                                void act(() => driveClient.downloadVersion(entry.path, row.id, row.name))
                              }
                              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title={t('drive.versionsDelete')}
                              onClick={() => void act(() => driveClient.deleteVersion(entry.path, row.id))}
                              className="rounded-md p-1.5 text-destructive transition-colors hover:bg-muted"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        }
                      />
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-nowrap items-center justify-between gap-3 border-t border-border-color px-4 py-3">
                <p className="min-w-0 truncate text-xs text-muted-foreground">{t('drive.versionsHint')}</p>
                <label
                  className={clsx(
                    'flex-shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90',
                    uploading && 'pointer-events-none opacity-60',
                  )}
                >
                  {uploading ? t('common.working') : t('drive.versionsUpload')}
                  <input
                    type="file"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (!file) return;
                      setUploading(true);
                      void act(async () => {
                        await driveClient.uploadNewVersion(entry.path, file);
                      }).finally(() => setUploading(false));
                    }}
                  />
                </label>
              </div>
              {error && <p className="border-t border-border-color px-4 py-2 text-sm text-destructive">{error}</p>}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function VersionRow({
  row,
  label,
  locale,
  actions,
}: {
  row: FileVersionRow;
  label: string;
  locale?: string;
  actions?: React.ReactNode;
}) {
  return (
    <li className="flex flex-nowrap items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="whitespace-nowrap text-sm text-foreground">
          {label}
          <span className="ms-2 text-xs text-muted-foreground">{formatBytes(row.size)}</span>
        </p>
        <p className="whitespace-nowrap text-xs text-muted-foreground">
          {formatTimestamp(new Date(row.createdAt ?? 0).getTime(), locale)}
        </p>
      </div>
      {actions}
    </li>
  );
}
